import type { DatafeedSocket } from './datafeedSocket'
import type { SubBarRow } from '../utils/formingBarBuffer'
import { getTimeframeSeconds, isKnownTimeframe } from '../../utils/chartUtils'

/**
 * Sub-bar supply for the Backtest chart's forming candle.
 *
 * Two paths, chosen by display timeframe (see `subBarSourceFor`):
 *
 *   'ticks' — real ticks from the .bi5 archive, for bars short enough that the
 *             tick count is affordable. Only real ticks can subdivide an M1 bar.
 *   'm1'    — the M1 bars the datafeed already serves, for everything longer.
 *             M15 gets 15 steps, H1 gets 60, D1 gets 1440 — no new server work.
 *
 * Both produce `SubBarRow[]`, so FormingBarBuffer folds them identically.
 */

/**
 * Longest bar we will subdivide with raw ticks, in seconds (M6).
 *
 * Measured density across the EURUSD tick archive (2015 onwards): the busiest
 * minute holds 752 ticks and the busiest hour 44,642 (the median hour ~3k). So
 * ticks per bar run M1≈750, M5≈3.7k, M15≈11k, H1≈45k — at 2x replay (4 bars/s)
 * that is 87 KB/s on M1 but 5.3 MB/s on H1. M6 (≈4.5k worst case) is the last
 * rung that fits a sane per-request budget, and above it M1 bars subdivide
 * perfectly well anyway.
 */
export const MAX_TICK_BAR_SECONDS = 360

/**
 * The same ceiling at REAL-TIME pacing (H1).
 *
 * MAX_TICK_BAR_SECONDS bounds ticks per second of REPLAY: at 2x an H1 bar's 45k ticks land in
 * 250ms, which is the 5.3 MB/s that drew the M6 line. Market-clock pacing spends a fraction of a
 * wall-clock hour on that same bar — the whole hour at 1x, six minutes at its fastest 10x — so
 * those ticks arrive at 12 to 124 a second, orders of magnitude under the bound being protected,
 * and the M6 line stops meaning anything.
 *
 * What it bounds instead is ONE REQUEST. An H1 bar is at most ~44.6k ticks, ~1.3 MB on the wire
 * (a typical one is a tenth of that); D1 would be ~1M rows and ~30 MB, so the ceiling stops at
 * H1. Above it a single candle takes four wall-clock hours anyway, and M1 rows subdivide it at
 * exactly one step per real minute. The datafeed's own MAX_TICK_SPAN_S is set to this same
 * 3600, so asking for more than one H1 bar at a time would come back short.
 */
export const REALTIME_MAX_TICK_BAR_SECONDS = 3600

export type SubBarSource = 'ticks' | 'm1' | 'none'

/**
 * Which supply path subdivides a bar of `timeframe`.
 *
 * `maxTickBarSeconds` is the ceiling to apply — the caller knows the pacing, this module knows
 * the densities. Defaulting it keeps every existing call site honest and unchanged.
 */
export function subBarSourceFor(
  timeframe: string,
  maxTickBarSeconds: number = MAX_TICK_BAR_SECONDS,
): SubBarSource {
  // An unrecognised timeframe has an unknown bar length, and getTimeframeSeconds
  // would quietly answer M1 — which would scatter the steps across the wrong span.
  if (!isKnownTimeframe(timeframe)) return 'none'
  const tfSec = getTimeframeSeconds(timeframe)
  if (tfSec <= maxTickBarSeconds) return 'ticks'
  return 'm1'
}

/** The timeframe used as the sub-divider on the 'm1' path. */
const SUB_BAR_TIMEFRAME = 'M1'

export interface SubBarFill {
  rows: SubBarRow[]
  /**
   * End of the window actually covered, unix seconds. A bar whose end exceeds
   * this was not fully served, so folding it would use an incomplete row set.
   */
  coveredTo: number
  /** False once the source is known not to serve this market at all. */
  available: boolean
}

/**
 * Raw ticks over `[fromTs, toTs)`, shaped as degenerate sub-bars (o=h=l=c=mid).
 *
 * The server may narrow the window when it hits its row cap, so `coveredTo` is
 * authoritative rather than `toTs`.
 */
export async function fetchTicks(
  socket: DatafeedSocket,
  symbol: string,
  fromTs: number,
  toTs: number,
): Promise<SubBarFill> {
  if (toTs <= fromTs) return { rows: [], coveredTo: fromTs, available: true }

  const reply = await socket.requestTicks({ symbol, from: fromTs, to: toTs })
  return {
    rows: reply.ticks.map(([timeMs, mid, volume]) => ({
      timeMs,
      open: mid,
      high: mid,
      low: mid,
      close: mid,
      volume,
    })),
    coveredTo: reply.to,
    // A market gap comes back as source 'ticks' with no rows; only 'none' means
    // the archive genuinely has nothing for this symbol.
    available: reply.source === 'ticks',
  }
}

/**
 * M1 bars covering `[fromTs, toTs)` — unix seconds, end EXCLUSIVE.
 *
 * The `history` frame's `to` is inclusive (the server filters `<= to`), so the
 * exclusive end is converted here. Getting this wrong would pull the next display
 * bar's first minute into the current bar's fold and contaminate its high/low.
 */
export async function fetchM1SubBars(
  socket: DatafeedSocket,
  symbol: string,
  fromTs: number,
  toTs: number,
): Promise<SubBarRow[]> {
  if (toTs <= fromTs) return []
  const rows = await socket.requestHistory({
    symbol,
    timeframe: SUB_BAR_TIMEFRAME,
    from: fromTs,
    to: toTs - 1,
  })
  return rows.map(([time, open, high, low, close, volume]) => ({
    timeMs: time * 1000,
    open,
    high,
    low,
    close,
    volume: volume ?? 0,
  }))
}
